import { getScanTone, type ScanDecision } from './scanInstructionTone';

interface InstructionStyle {
    label: string;
    /** Full explanation of DM's instruction, shown on hover. */
    title: string;
}

// DM's original instruction names, kept as-is since doctors know them from the
// DM dashboard; the hover title spells out what each decision means.
const INSTRUCTION_PILL_STYLES: Record<ScanDecision, InstructionStyle> = {
    go: {
        label: 'Go',
        title: 'The patient can move to the next aligner'
    },
    nogo: {
        label: 'No-Go',
        title: 'The patient should stay on the current aligner'
    },
    go_back: {
        label: 'Go-Back',
        title: 'The patient should go back to a previous aligner'
    },
    start: {
        label: 'Start',
        title: 'First scan of the series. Not a Go/No-Go decision.'
    }
};

// Compact by design: these share a line with the scan identity and date in
// headers that narrow a long way.
const PILL_BASE =
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold leading-4';
const PILL_DOT = 'w-1 h-1 rounded-full bg-current opacity-40 flex-shrink-0';

interface InstructionPillProps {
    decision: ScanDecision | null;
}

function InstructionPill({ decision }: InstructionPillProps) {
    if (!decision) return null;
    const style = INSTRUCTION_PILL_STYLES[decision];
    return (
        <span className={`${PILL_BASE} ${getScanTone(decision).pill}`} title={style.title}>
            <span className={PILL_DOT} />
            {style.label}
        </span>
    );
}

interface LatePillProps {
    late: boolean | undefined;
    daysLate: number | null | undefined;
}

function LatePill({ late, daysLate }: LatePillProps) {
    if (!late) return null;
    const daysSuffix = daysLate != null && daysLate > 0 ? ` · ${daysLate}d` : '';
    const titleText =
        daysLate != null && daysLate > 0
            ? `Uploaded ${daysLate} day${daysLate > 1 ? 's' : ''} after the due date`
            : 'Scan uploaded after the due date';
    return (
        <span className={`${PILL_BASE} bg-amber-50 text-amber-700`} title={titleText}>
            <span className={PILL_DOT} />
            LATE{daysSuffix}
        </span>
    );
}

export { InstructionPill, LatePill };
